import styled from '@emotion/styled';
import { motion } from 'framer-motion';

// Base Button Styles - zentral für alle Buttons
export const baseButtonStyles = (colors: any) => `
  width: 45px;
  height: 45px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid ${colors.primary};
  color: ${colors.primary};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  @media (max-width: 1100px) {
    transition: none;
  }
  
  @media (min-width: 768px) and (max-width: 1024px) {
    width: 60px;
    height: 60px;
  }
  
  backdrop-filter: blur(5px);

  &:hover {
    background: ${colors.primary};
    color: white;
  }

  svg {
    stroke: ${colors.primary};
    transition: all 0.3s ease;
  
  @media (max-width: 1100px) {
    transition: none;
  }
  
  @media (min-width: 768px) and (max-width: 1024px) {
    width: 28px;
    height: 28px;
  }
  }
  
  &:hover svg {
    stroke: white;
  }
`;

export const GlobalStyle = styled.div`
  /* Normal cursor */
`;

export const Header = styled.header<{ isScrolled: boolean; colors: any; menuItemCount: number; scrollY?: number }>`
  position: fixed;
  top: ${props => props.scrollY !== undefined ? `${Math.max(20 - props.scrollY, -100)}px` : '20px'};
  left: 50%;
  right: auto;
  transform: translateX(-50%) translateY(${props => props.isScrolled ? '-200%' : '0'});
  opacity: ${props => props.isScrolled ? 0 : 1};
  height: 60px;
  min-height: 60px;
  max-height: 60px;
  background: ${props => props.colors.bg === '#1a1a1a' ? 'rgba(20, 20, 20, 0.1)' : 'rgba(255, 255, 255, 0.1)'};
  backdrop-filter: blur(5px);
  border: 2px solid ${props => props.colors.primary};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 12px 20px;
  z-index: 1000;
  transition: all 0.3s ease;
  
  @media (max-width: 1100px) {
    transition: none;
  }
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  box-sizing: border-box;
  gap: 15px;
  overflow: visible;
  width: fit-content;
  max-width: calc(100vw * 0.35);
  min-width: max-content;
  
  @media (max-width: 1200px) {
    padding: 12px 18px;
    gap: 12px;
    max-width: calc(100vw * 0.4);
    height: 60px;
    min-height: 60px;
    max-height: 60px;
  }
  
  @media (max-width: 1100px) {
    left: auto;
    right: 40px;
    top: ${props => {
      if (props.scrollY === undefined) return '15px';
      // Header scrollt nach oben wenn man nach unten scrollt
      const newTop = 15 - props.scrollY;
      return `${Math.max(newTop, -100)}px`;
    }};
    transform: translateY(0);
    opacity: ${props => {
      // Header wird unsichtbar wenn er aus dem Viewport ist
      if (props.scrollY === undefined) return 1;
      const headerTop = 15 - props.scrollY;
      return headerTop < -50 ? 0 : 1;
    }};
    pointer-events: ${props => {
      if (props.scrollY === undefined) return 'auto';
      const headerTop = 15 - props.scrollY;
      return headerTop < -50 ? 'none' : 'auto';
    }};
    padding: 12px 16px;
    max-width: fit-content;
    width: auto;
    height: 56px;
    min-height: 56px;
    gap: 12px;
    justify-content: flex-start;
    align-items: center;
    transition: none !important;
    border: none !important;
    background: transparent !important;
    backdrop-filter: none !important;
    box-shadow: none !important;
    
    .desktop-logo {
      display: none !important;
    }
    
    .mobile-logo {
      display: block !important;
      height: 80px !important;
      margin-right: 8px !important;
      margin-left: 0 !important;
      margin-top: 0 !important;
      margin-bottom: 0 !important;
      position: relative;
      object-position: right center !important;
    }
    
    .mobile-logo-static {
      display: block !important;
      height: 40px !important;
      margin-right: 8px !important;
      margin-left: 0 !important;
      margin-top: 0 !important;
      margin-bottom: 0 !important;
      position: relative;
    }
  }
  
  /* Tablet: Nur wenn Breite zwischen 768px und 1100px (nicht Desktop) */
  @media (min-width: 768px) and (max-width: 1100px) {
    height: 56px;
    min-height: 56px;
    max-height: 56px;
    padding: 12px 16px;
    gap: 12px;
    
    .mobile-logo {
      height: 80px !important;
    }
    
    .mobile-logo-static {
      height: 40px !important;
    }
  }
  
  @media (max-width: 768px) {
    top: 15px;
    padding: 10px 14px;
    height: 52px;
    min-height: 52px;
    right: 35px;
    gap: 10px;
    
    .mobile-logo {
      height: 72px !important;
      margin-right: 6px !important;
    }
    
    .mobile-logo-static {
      height: 36px !important;
      margin-right: 6px !important;
    }
  }
  
  @media (max-width: 480px) {
    right: 30px;
    padding: 8px 12px;
    height: 48px;
    min-height: 48px;
    gap: 8px;
    
    .mobile-logo {
      height: 64px !important;
      margin-right: 6px !important;
    }
    
    .mobile-logo-static {
      height: 32px !important;
      margin-right: 6px !important;
    }
  }
  
  /* Desktop: Header-Höhe bleibt IMMER 60px, unabhängig von Fensterhöhe oder anderen Faktoren */
  @media (min-width: 1101px) {
    height: 60px !important;
    min-height: 60px !important;
    max-height: 60px !important;
  }
`;

export const Main = styled.main`
  padding-top: 0;
`;

export const HeaderCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  gap: clamp(12px, 1.5vw, 25px);
  min-width: max-content;
  overflow: visible;
  
  @media (max-width: 1200px) {
    gap: clamp(10px, 1.2vw, 18px);
  }
  
  @media (max-width: 1100px) {
    margin-left: auto;
    flex: 0;
    justify-content: flex-end;
  }
`;

export const Nav = styled.nav`
  display: flex;
  gap: clamp(12px, 1.5vw, 25px);
  align-items: center;
  flex-wrap: nowrap;
  overflow: visible;
  min-width: max-content;
  
  @media (max-width: 1200px) {
    gap: clamp(10px, 1.2vw, 18px);
  }
  
  @media (max-width: 1100px) {
    display: none;
  }
`;

export const MobileMenuButton = styled.button<{ colors: any }>`
  display: none;
  background: transparent;
  border: none;
  color: ${props => props.colors.primary};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  margin: 0;
  transition: all 0.3s ease;
  
  @media (max-width: 1100px) {
    transition: none;
  }
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 24px;
    height: 24px;
    display: block;
  }
  
  &:hover {
    color: ${props => props.colors.primary};
    opacity: 0.8;
  }
  
  @media (max-width: 1100px) {
    display: flex;
  }
`;

export const NavLink = styled.a<{ active: boolean; colors: any }>`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.7rem, 0.9vw, 0.85rem);
  font-weight: ${props => props.active ? '700' : '500'};
  color: ${props => props.colors.primary};
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  @media (max-width: 1100px) {
    transition: none;
  }
  position: relative;
  letter-spacing: 0px;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: fit-content;
  
  &:hover {
    color: white;
  }
  
  @media (max-width: 1300px) {
    font-size: clamp(0.68rem, 0.85vw, 0.82rem);
  }
  
  @media (max-width: 1200px) {
    font-size: clamp(0.65rem, 0.8vw, 0.8rem);
  }
  
  @media (max-width: 1150px) {
    font-size: clamp(0.63rem, 0.75vw, 0.78rem);
  }
`;

export const DropdownContainer = styled.div`
  position: relative;
`;

export const DropdownButton = styled.div<{ colors: any; isOpen: boolean }>`
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  font-weight: 500;
  color: ${props => props.colors.textPrimary};
  cursor: pointer;
  transition: all 0.3s ease;
  
  @media (max-width: 1100px) {
    transition: none;
  }
  position: relative;
  display: flex;
  align-items: center;
  gap: 5px;
  
  &::after {
    content: '▼';
    font-size: 0.7rem;
    transition: transform 0.3s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
  
  &:hover {
    color: ${props => props.colors.primary};
  }
`;

export const DropdownMenu = styled(motion.div)<{ colors: any }>`
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  background: ${props => props.colors.bgCard};
  backdrop-filter: blur(10px);
  border: 1px solid ${props => props.colors.headerBorder};
  border-radius: 8px;
  padding: 10px 0;
  min-width: 200px;
  box-shadow: 0 10px 40px rgba(107, 68, 35, 0.3);
  z-index: 1000;
`;

export const DropdownItem = styled.a<{ colors: any }>`
  display: block;
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${props => props.colors.textSecondary};
  text-decoration: none;
  padding: 10px 20px;
  transition: all 0.3s ease;
  
  @media (max-width: 1100px) {
    transition: none;
  }
  
  &:hover {
    color: ${props => props.colors.primary};
    background: ${props => props.colors.bgCardHover};
    padding-left: 25px;
  }
`;

export const MobileButtonBar = styled.div`
  display: none;
  
  @media (max-width: 1100px) {
    display: flex;
    position: fixed;
    bottom: 20px;
    left: 20px;
    right: 20px;
    justify-content: space-between;
    align-items: center;
    z-index: 1001;
    pointer-events: none;
    
    > * {
      pointer-events: auto;
    }
  }
`;

export const ScrollToTopButton = styled(motion.button)<{ colors: any; scrollProgress: number }>`
  ${props => baseButtonStyles(props.colors)}
  position: fixed;
  bottom: 77px;
  right: 20px;
  z-index: 1000;
  font-size: 1.2rem;
  font-weight: bold;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;

  @media (max-width: 1100px) {
    bottom: 77px;
    right: 20px;
  }
  
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 1.5rem;
  }
`;

export const MenuPopupButton = styled(motion.button)<{ colors: any }>`
  ${props => baseButtonStyles(props.colors)}
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  padding: 0;
  overflow: hidden;

  svg {
    width: 24px;
    height: 24px;
  }

  
  @media (max-width: 1100px) {
    display: none;
  }
`;

export const MenuPopupOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 10001;
  backdrop-filter: blur(5px);
`;

export const MenuPopupContainer = styled(motion.div)<{ colors: any }>`
  position: fixed;
  top: 50%;
  left: 50%;
  width: 500px;
  height: 500px;
  z-index: 10002;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    width: 400px;
    height: 400px;
  }
  
  @media (max-width: 480px) {
    width: 350px;
    height: 350px;
  }
`;

export const MenuWrapper = styled(motion.div)<{ colors: any }>`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
`;

export const LogoCircle = styled.div<{ colors: any }>`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 160px;
  height: 160px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  pointer-events: none;
  overflow: visible;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
  
  @media (max-width: 1100px) {
    width: 128px;
    height: 128px;
  }
  
  @media (max-width: 768px) {
    width: 96px;
    height: 96px;
  }
  
  @media (max-width: 480px) {
    width: 96px;
    height: 96px;
  }
`;

export const MenuButton = styled.button<{ x: number; y: number; active: boolean; colors: any; counterRotation: number }>`
  position: absolute;
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  width: 180px;
  height: 48px;
  background: ${props => props.active 
    ? props.colors.primary
    : (props.colors.bg === '#1a1a1a' ? 'rgba(20, 20, 20, 0.1)' : 'rgba(255, 255, 255, 0.1)')};
  border: 2px solid ${props => props.colors.primary};
  color: ${props => props.active ? 'white' : props.colors.primary};
  font-weight: ${props => props.active ? 'bold' : '600'};
  font-size: 1rem;
  font-family: 'Poppins', sans-serif;
  text-align: center;
  cursor: pointer;
  z-index: 3;
  transition: all 0.3s ease;
  
  @media (max-width: 1100px) {
    transition: none;
  }
  transform: translate(-50%, -50%) rotate(${props => props.counterRotation}deg);
  text-shadow: none;
  border-radius: 12px;
  backdrop-filter: blur(5px);
  pointer-events: auto;
  
  &:hover {
    background: ${props => props.colors.primary};
    color: white;
    font-size: 1.05rem;
    font-weight: bold;
    transform: translate(-50%, -50%) rotate(${props => props.counterRotation}deg);
  }
  
  @media (max-width: 768px) {
    width: 140px;
    height: 40px;
    font-size: 0.8rem;
    
    &:hover {
      font-size: 0.85rem;
    }
  }
  
  @media (max-width: 480px) {
    width: 120px;
    height: 38px;
    font-size: 0.75rem;
    
    &:hover {
      font-size: 0.8rem;
    }
  }
`;

export const ContactFabGroup = styled.div<{ colors: any }>`
  position: fixed;
  top: 177px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 1000;

  @media (max-width: 1100px) {
    display: none;
  }
`;

export const SearchButton = styled(motion.button)<{ colors: any }>`
  ${props => baseButtonStyles(props.colors)}
  position: fixed;
  top: 291px;
  right: 20px;
  z-index: 1000;

  svg {
    width: 20px;
    height: 20px;
    stroke-width: 2.5;
  }

  @media (max-width: 1100px) {
    display: none;
  }
`;

export const LanguageToggle = styled(motion.button)<{ colors: any }>`
  ${props => baseButtonStyles(props.colors)}
  position: fixed;
  top: 122px;
  right: 20px;
  z-index: 1000;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 0.9rem;

  @media (max-width: 1100px) {
    display: none;
  }
`;

export const AccessibilityButton = styled(motion.button)<{ colors: any }>`
  ${props => baseButtonStyles(props.colors)}
  position: fixed;
  top: 348px;
  right: 20px;
  z-index: 1000;

  svg {
    width: 34px;
    height: 34px;
  }

  @media (max-width: 1100px) {
    display: none;
  }
`;

export const ContactFab = styled(motion.a)<{ colors: any }>`
  ${props => baseButtonStyles(props.colors)}
  display: inline-flex;
  text-decoration: none;
  
  @media (max-width: 768px) {
    position: relative;
  }
`;

export const MobileSearchButton = styled(motion.button)<{ colors: any }>`
  ${props => baseButtonStyles(props.colors)}
  position: relative;
  z-index: 1000;

  svg {
    width: 20px;
    height: 20px;
    stroke-width: 2.5;
  }
  
  @media (min-width: 768px) and (max-width: 1024px) {
    width: 60px;
    height: 60px;
    
    svg {
      width: 28px;
      height: 28px;
    }
  }
`;

export const MobileAccessibilityButton = styled(motion.button)<{ colors: any }>`
  ${props => baseButtonStyles(props.colors)}
  position: relative;
  z-index: 1000;

  svg {
    width: 34px;
    height: 34px;
  }
  
  @media (min-width: 768px) and (max-width: 1024px) {
    width: 60px;
    height: 60px;
    
    svg {
      width: 34px;
      height: 34px;
    }
  }
`;

export const MobileMenuButtonBar = styled(motion.button)<{ colors: any }>`
  ${props => baseButtonStyles(props.colors)}
  position: relative;
  z-index: 1000;
  padding: 0;
  overflow: hidden;

  svg {
    width: 24px;
    height: 24px;
  }

  
  @media (min-width: 768px) and (max-width: 1024px) {
    width: 60px;
    height: 60px;
    
    svg {
      width: 32px;
      height: 32px;
    }
  }
`;

export const MobileLanguageToggle = styled(motion.button)<{ colors: any }>`
  ${props => baseButtonStyles(props.colors)}
  position: relative;
  z-index: 1000;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 0.9rem;
  
  @media (min-width: 768px) and (max-width: 1024px) {
    width: 60px;
    height: 60px;
    font-size: 1.4rem;
  }
`;

export const LayoutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

export const Background = styled.div<{ colors: any }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: ${props => props.colors.bg};
  z-index: -10;
  pointer-events: none;
`;

export const HeaderVideoContainer = styled.div<{ isScrolled: boolean; colors: any }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  /* Bild Aspect Ratio: 3560x2478 = 1.436:1, Höhe = Breite / 1.436 */
  height: calc(100vw / 1.436);
  min-height: 100vh;
  z-index: -9;
  overflow: hidden;
  pointer-events: none;
  opacity: ${props => props.isScrolled ? 0 : 1};
  transition: opacity 0.3s ease;
  display: block;
  background: ${props => props.colors.bg};
  
  @media (max-width: 1100px) {
    height: calc(100vw / 1.436);
    min-height: 100vh;
    transition: none;
    opacity: ${props => props.isScrolled ? 0 : 1};
    pointer-events: ${props => props.isScrolled ? 'none' : 'auto'};
    z-index: 5;
  }
  
  > * {
    pointer-events: auto;
  }
`;

export const HeroContent = styled(motion.div)<{ colors: any; isDarkMode: boolean; isScrolled: boolean }>`
  position: absolute !important;
  /* Desktop: 20vh vom Top des HeroSection */
  top: 20vh !important;
  left: 50% !important;
  right: auto !important;
  transform: translateX(-50%) !important;
  z-index: 10000 !important;
  padding: 0 !important;
  margin: 0 !important;
  background: transparent !important;
  backdrop-filter: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  border: none !important;
  pointer-events: auto !important;
  user-select: text !important;
  -webkit-user-select: text !important;
  -moz-user-select: text !important;
  -ms-user-select: text !important;
  box-sizing: border-box !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  width: fit-content !important;
  max-width: calc(100vw * 0.35) !important;
  min-width: max-content !important;
  
  * {
    user-select: text !important;
    -webkit-user-select: text !important;
    -moz-user-select: text !important;
    -ms-user-select: text !important;
  }
  
  @media (max-width: 1200px) {
    max-width: calc(100vw * 0.4) !important;
  }
  
  @media (max-width: 1100px) {
    display: none !important;
  }
`;

export const HeroTitle = styled(motion.h1)<{ colors: any }>`
  font-family: 'Cookie', cursive;
  font-size: clamp(1.2rem, 3vw, 4rem);
  font-weight: 400;
  color: ${props => props.colors.primary};
  margin: -10px auto 20px auto;
  padding: 0;
  transition: color 2s ease-in-out;
  user-select: text !important;
  -webkit-user-select: text !important;
  -moz-user-select: text !important;
  -ms-user-select: text !important;
  pointer-events: auto;
  text-align: center;
  white-space: nowrap;
  overflow: visible;
  width: fit-content;
  box-sizing: border-box;
  
  @media (max-width: 1400px) {
    font-size: clamp(1.1rem, 2.8vw, 3.5rem);
  }
  
  @media (max-width: 1200px) {
    font-size: clamp(1rem, 2.5vw, 3rem);
  }
  
  @media (max-width: 1100px) {
    display: none;
  }
`;

export const HeroSubtitle = styled(motion.p)<{ colors: any }>`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.75rem, 1.2vw, 1.2rem);
  font-weight: 400;
  line-height: 1.6;
  color: ${props => props.colors.primary};
  margin: 20px auto 60px auto;
  padding: 0;
  transition: color 2s ease-in-out;
  opacity: 1;
  user-select: text !important;
  -webkit-user-select: text !important;
  -moz-user-select: text !important;
  -ms-user-select: text !important;
  pointer-events: auto;
  white-space: pre-line;
  text-align: center;
  max-width: fit-content;
  width: fit-content;
  
  .mobile-text {
    display: none;
  }
  
  .desktop-text {
    display: inline;
  }
  
  @media (max-width: 1100px) {
    flex: 0;
    margin: 0;
    text-align: center;
    font-size: 0.95rem;
    line-height: 1.5;
    color: ${props => props.colors.primary};
    max-width: none;
    width: 100%;
    
    .mobile-text {
      display: block;
    }
    
    .desktop-text {
      display: none;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    line-height: 1.4;
  }
`;

export const CTAButton = styled(motion.button)<{ colors: any }>`
  display: block;
  margin: 0 auto;
  padding: 14px 28px;
  background: ${props => props.colors.bg === '#1a1a1a' ? 'rgba(30, 30, 30, 0.5)' : 'rgba(255, 255, 255, 0.5)'};
  color: ${props => props.colors.primary};
  border: 2px solid ${props => props.colors.primary};
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.8rem, 1vw, 0.95rem);
  font-weight: 500;
  text-decoration: none;
  border-radius: 12px;
  transition: all 0.3s ease;
  
  @media (max-width: 1100px) {
    transition: none;
  }
  backdrop-filter: blur(15px);
  cursor: pointer !important;
  pointer-events: auto !important;
  position: relative;
  z-index: 10001 !important;
  user-select: none;
  width: fit-content;
  
  &:hover {
    background: ${props => props.colors.primary};
    color: ${props => props.colors.textPrimary};
  }
  
  @media (max-width: 1100px) {
    margin: 0;
    padding: 12px 28px;
    font-size: 0.9rem;
    flex-shrink: 0;
    background: ${props => props.colors.primary};
    color: white;
    width: auto;
    align-self: center;
    
    &:hover {
      background: ${props => props.colors.primaryHover};
      color: white;
    }
  }
  
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 1.2rem;
    padding: 16px 36px;
  }
  
  @media (max-width: 480px) {
    padding: 10px 24px;
    font-size: 0.85rem;
  }
`;

export const HeroLogoDesktopGif = styled(motion.img)`
  display: none;
  
  @media (min-width: 1101px) {
    display: block;
    width: auto;
    height: clamp(120px, 12vw, 120px);
    object-fit: contain;
    margin-bottom: -10px;
  }
`;

export const HeroLogoDesktopPng = styled(motion.img)`
  display: none;
  
  @media (min-width: 1101px) {
    display: block;
    width: auto;
    height: clamp(55px, 5vw, 55px);
    object-fit: contain;
    margin-bottom: -10px;
  }
`;

export const HeroLogoMobileGif = styled(motion.img)`
  display: none;
  
  @media (max-width: 1100px) {
    display: block;
    width: auto;
    height: clamp(120px, 12vw, 120px);
    object-fit: contain;
    margin-bottom: -10px;
  }
  
  @media (min-width: 768px) and (max-width: 1024px) {
    height: clamp(120px, 12vw, 120px);
  }
`;

export const HeroLogoMobilePng = styled(motion.img)`
  display: none;
  
  @media (max-width: 1100px) {
    display: block;
    width: auto;
    height: clamp(55px, 5vw, 55px);
    object-fit: contain;
    margin-bottom: -10px;
  }
  
  @media (min-width: 768px) and (max-width: 1024px) {
    height: clamp(55px, 5vw, 55px);
  }
`;

export const MobileContentContainer = styled(motion.div)`
  display: none;
  
  @media (max-width: 1100px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: absolute;
    /* Mobile: 50vh vom Top des HeroSection */
    top: 50vh;
    left: 35%;
    z-index: 10000;
    width: auto;
    max-width: calc(100vw - 28%);
  }
`;

export const MobileTagline = styled(motion.div)<{ colors: any }>`
  display: none;
  
  @media (max-width: 1100px) {
    display: block;
    font-family: 'Cookie', cursive;
    font-size: clamp(2rem, 4vw, 2.8rem);
    font-weight: 400;
    color: ${props => props.colors.primary};
    text-align: center;
    line-height: 1.4;
    white-space: nowrap;
    margin: -10px 0 0 0;
    width: 100%;
  }
  
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: clamp(3rem, 6vw, 4rem);
  }
`;

export const MobileHeroSubtitle = styled(motion.p)<{ colors: any }>`
  display: none;
  
  @media (max-width: 1100px) {
    display: block;
    font-family: 'Poppins', sans-serif;
    font-size: clamp(0.9rem, 1.8vw, 1.1rem);
    font-weight: 400;
    line-height: 1.6;
    color: ${props => props.colors.primary};
    text-align: center;
    white-space: pre-line;
    width: 100%;
    margin-top: 20px;
    margin-left: 0;
    margin-right: 0;
  }
  
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: clamp(1.3rem, 2.5vw, 1.6rem);
  }
`;

export const MobileCTAButton = styled(motion.button)<{ colors: any }>`
  display: none;
  
  @media (max-width: 1100px) {
    display: block;
    padding: clamp(10px, 1.5vw, 14px) clamp(24px, 3vw, 32px);
    font-size: clamp(0.85rem, 1.5vw, 1rem);
    background: ${props => props.colors.primary};
    color: white;
    border: none;
    border-radius: 12px;
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 20px;
    margin-left: 0;
    margin-right: 0;
    
    &:hover {
      background: ${props => props.colors.primaryHover};
      transform: scale(1.05);
    }
  }
  
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: clamp(1.2rem, 2.5vw, 1.5rem);
    padding: clamp(14px, 2.5vw, 20px) clamp(32px, 5vw, 48px);
  }
`;
